/*
 * Copyright (c) 2026. Orion Analytics. All Rights Reserved
 */

/*
 * Orion Analytics
 * Global Heatmap
 */

import React, { useMemo } from "react";

const PERIODS = {
    "1M": 30,
    "3M": 90,
    "6M": 180,
    "1Y": 365
};

export default function GlobalHeatmap({ histData }) {

const symbols = useMemo(() => {

    const HEATMAP_SYMBOLS = [

        // India
        "NIFTY 50",

        // US
        "S&P500",
        "NASDAQ",

        // Europe
        "FTSE",
        "DAX",

        // Asia
        "Nikkei225",
        "HANGSENG",

        // FX
        "USDINR",
        "EURUSD",

        // Commodities
        "Gold",
        "Silver",
        "WTI_Crude"

    ];

    return HEATMAP_SYMBOLS.filter(
        symbol =>
            histData.some(
                r => r.symbol === symbol
            )
    );

}, [histData]);

    const calcReturn = (rows, days) => {

        if (!rows || rows.length < 2)
            return null;

        const sorted = [...rows]
            .sort(
                (a, b) =>
                    new Date(a.date) -
                    new Date(b.date)
            );

        const latest =
            sorted[sorted.length - 1];

        const cutoff =
            Date.now() -
            days * 86400000;

        const start =
            sorted.find(
                r =>
                    new Date(r.date)
                        .getTime() >= cutoff
            );

        if (!start)
            return null;

        return (
            (
                latest.close -
                start.close
            ) /
            start.close
        ) * 100;
    };

    const heatmapData = symbols.map(symbol => {

        const rows =
            histData.filter(
                r => r.symbol === symbol
            );

        return {
            symbol,
            "1M": calcReturn(rows, 30),
            "3M": calcReturn(rows, 90),
            "6M": calcReturn(rows, 180),
            "1Y": calcReturn(rows, 365)
        };

    });

    const cellColor = value => {

        if (value === null)
            return "#1e293b";

        if (value > 20)
            return "#16a34a";

        if (value > 10)
            return "#22c55e";

        if (value > 0)
            return "#4ade80";

        if (value > -10)
            return "#f97316";

        return "#dc2626";
    };

    return (

        <div
            className="
                bg-slate-950
                border
                border-slate-800
                rounded-3xl
                p-6
            "
        >

            <h2
                className="
                    text-xl
                    font-semibold
                    text-white
                    mb-6
                "
            >
                Global Market Heatmap
            </h2>

            <div className="overflow-x-auto">

                <table
                    className="
                        min-w-full
                        border-collapse
                    "
                >

                    <thead>

                    <tr>

                        <th
                            className="
                                text-left
                                text-slate-400
                                p-3
                            "
                        >
                            Asset
                        </th>

                        {
                            Object.keys(PERIODS)
                                .map(period => (

                                    <th
                                        key={period}
                                        className="
                                            text-center
                                            text-slate-400
                                            p-3
                                        "
                                    >
                                        {period}
                                    </th>

                                ))
                        }

                    </tr>

                    </thead>

                    <tbody>

                    {
                        heatmapData.map(row => (

                            <tr
                                key={row.symbol}
                            >

                                <td
                                    className="
                                        p-3
                                        text-white
                                        font-medium
                                    "
                                >
                                    {row.symbol}
                                </td>

                                {
                                    Object.keys(PERIODS)
                                        .map(period => (

                                            <td
                                                key={period}
                                                className="
                                                    p-3
                                                    text-center
                                                    font-semibold
                                                    text-white
                                                "
                                                style={{
                                                    backgroundColor:
                                                        cellColor(
                                                            row[
                                                                period
                                                                ]
                                                        )
                                                }}
                                            >
                                                {
                                                    row[
                                                        period
                                                        ]
                                                        ?.toFixed(1)
                                                }
                                                %
                                            </td>

                                        ))
                                }

                            </tr>

                        ))
                    }

                    </tbody>

                </table>

            </div>

        </div>

    );

}